import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Save } from 'lucide-react';
import { reachTrackerAPI, ReachTrackerEntry, ReachScore } from '../../../services/reachService';
import { REACHScoreInput } from '../common/REACHScoreInput';

type StageKey = 'r' | 'e' | 'a' | 'c' | 'h';
type LayerKey = 'individual' | 'org';
type ScoreField = `${StageKey}_${LayerKey}`;

interface Props {
  projectId: string;
  week: number;
  allWeeks: number[];
}

const STAGES: StageKey[] = ['r', 'e', 'a', 'c', 'h'];
const STAGE_LABELS: Record<StageKey, string> = {
  r: 'Recognise / Rally',
  e: 'Engage / Enable',
  a: 'Acquire / Architect',
  c: 'Capability / Cultivate',
  h: 'Habits / Hardwire',
};
const STAGE_COLORS: Record<StageKey, string> = {
  r: '#f59e0b', e: '#3b82f6', a: '#8b5cf6', c: '#10b981', h: '#ec4899',
};

function stageAvg(entry: ReachTrackerEntry, stage: StageKey): number | undefined {
  const ind = entry.scores?.[`${stage}_individual`];
  const org = entry.scores?.[`${stage}_org`];
  if (ind == null && org == null) return undefined;
  const vals = [ind, org].filter((v): v is ReachScore => v != null);
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

export const TrackerTab: React.FC<Props> = ({ projectId, week, allWeeks }) => {
  const [entries, setEntries] = useState<ReachTrackerEntry[]>([]);
  const [scores, setScores] = useState<Partial<Record<ScoreField, ReachScore | null>>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    reachTrackerAPI.list(projectId).then(r => setEntries(r.data.tracker));
  }, [projectId]);

  useEffect(() => {
    const existing = entries.find(e => e.week === week);
    setScores(existing ? { ...existing.scores } : {});
  }, [week, entries]);

  const save = async () => {
    setSaving(true);
    try {
      const entry: ReachTrackerEntry = {
        week,
        scores: Object.fromEntries(
          Object.entries(scores).filter(([, v]) => v != null)
        ) as ReachTrackerEntry['scores'],
        updatedAt: new Date().toISOString(),
        updatedBy: '',
      };
      await reachTrackerAPI.putWeek(projectId, week, entry);
      setEntries(prev => {
        const idx = prev.findIndex(e => e.week === week);
        if (idx >= 0) { const copy = [...prev]; copy[idx] = entry; return copy; }
        return [...prev, entry].sort((a, b) => a.week - b.week);
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  const chartData = allWeeks.map(w => {
    const e = entries.find(x => x.week === w);
    const point: Record<string, any> = { week: `W${w}` };
    if (e) STAGES.forEach(s => { point[s.toUpperCase()] = stageAvg(e, s); });
    return point;
  });

  const hasChart = entries.length > 0;

  let saveButtonLabel: string;
  if (saved) saveButtonLabel = 'Saved!';
  else if (saving) saveButtonLabel = 'Saving…';
  else saveButtonLabel = 'Save scores';

  return (
    <div className="flex flex-col gap-6">
      {hasChart && (
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-4">
          <p className="text-xs font-bold text-navy uppercase tracking-wide mb-4">Adoption Tracker</p>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis domain={[0, 4]} ticks={[1, 2, 3, 4]} tick={{ fontSize: 11, fill: '#64748b' }} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                formatter={(v: any) => (v != null ? String(v) : 'n/a')}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {STAGES.map(s => (
                <Line key={s} type="monotone" dataKey={s.toUpperCase()}
                  stroke={STAGE_COLORS[s]} strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[#e2e8f0] bg-slate-50/50 flex items-center justify-between flex-wrap gap-2">
          <span className="text-xs font-bold text-navy uppercase tracking-wide">Week {week} — Scores</span>
          <div className="flex items-center gap-3 flex-wrap">
            {([1, 2, 3, 4] as const).map(score => {
              const dotClass = ['bg-red-500', 'bg-amber-400', 'bg-blue-500', 'bg-green-500'][score - 1];
              const labels = ['Not yet', 'Emerging', 'Established', 'Embedded'];
              return (
                <div key={score} className="flex items-center gap-1">
                  <span className={`w-5 h-5 rounded text-white text-xs font-bold flex items-center justify-center ${dotClass}`}>{score}</span>
                  <span className="text-[10px] text-slate-400">{labels[score - 1]}</span>
                </div>
              );
            })}
            <span className="text-[10px] text-slate-400 ml-2">Leave blank if stage not active</span>
          </div>
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[#e2e8f0]">
              <th className="w-44 px-4 py-2.5 text-left bg-slate-50/50" />
              <th className="px-4 py-2.5 text-left">
                <span className="text-[10px] font-bold text-primary tracking-widest uppercase">Individual</span>
              </th>
              <th className="px-4 py-2.5 text-left border-l border-[#e2e8f0]">
                <span className="text-[10px] font-bold text-primary tracking-widest uppercase">Organisational</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#e2e8f0]">
            {STAGES.map(s => (
              <tr key={s}>
                <td className="px-4 py-3 bg-slate-50/50 align-middle">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                      style={{ backgroundColor: STAGE_COLORS[s] }}
                    >
                      {s.toUpperCase()}
                    </span>
                    <p className="text-xs font-semibold text-slate-500 leading-tight">{STAGE_LABELS[s]}</p>
                  </div>
                </td>
                {(['individual', 'org'] as LayerKey[]).map((layer, li) => (
                  <td key={layer} className={`px-4 py-3 align-middle ${li > 0 ? 'border-l border-[#e2e8f0]' : ''}`}>
                    <div className="flex items-center gap-2">
                      <REACHScoreInput
                        value={scores[`${s}_${layer}`] ?? null}
                        onChange={v => setScores({ ...scores, [`${s}_${layer}`]: v })}
                      />
                      {scores[`${s}_${layer}`] != null && (
                        <button
                          onClick={() => setScores({ ...scores, [`${s}_${layer}`]: null })}
                          className="text-[10px] text-slate-400 hover:text-red-400"
                        >clear</button>
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-3 border-t border-[#e2e8f0] bg-slate-50/50 flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 rounded-xl h-9 px-5 bg-primary hover:bg-primary-dark text-white text-sm font-bold shadow-sm transition-colors disabled:opacity-60"
          >
            <Save size={14} /> {saveButtonLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
